/**
 * Migraciones encadenadas n → n+1 (ADR 0008, doc. 03 §5). Ningún proyecto se invalida en
 * silencio: si una migración falla, el original queda intacto y se informa qué falló.
 *
 * - 1 → 2 (cierre de la 1.0): política «lavado después de hora» y réplicas automáticas. Los
 *   proyectos anteriores conservan su comportamiento: lavado que sigue el turno y réplicas fijas.
 * - 2 → 3 (paso 1.2): cada servicio dice en qué tipos de elevador se atiende (antes lo fijaba el
 *   catálogo de construcción). Se conserva lo de antes: el mixto atiende todo, el Fast Lane solo
 *   «mantenimiento» y el mayor solo «otros».
 * - 3 → 4 (paso 1.4): cada layout suma sus áreas de piso y sus muros; un proyecto anterior no tenía
 *   ninguno.
 */

import { VERSION_ESQUEMA } from '../nucleo/dominio/espacio';

export type Migracion = (documento: Record<string, unknown>) => Record<string, unknown>;

/** MIGRACIONES[n] convierte un documento de la versión n a la n + 1. */
export const MIGRACIONES: Readonly<Record<number, Migracion>> = {
  1: (doc) => {
    const espacios = Array.isArray(doc.espacios) ? (doc.espacios as Record<string, unknown>[]) : [];
    return {
      ...doc,
      espacios: espacios.map((e) => {
        const modelo = (e.modelo ?? {}) as Record<string, unknown>;
        const politicas = (modelo.politicas ?? {}) as Record<string, unknown>;
        const aleatoria = (e.configuracionAleatoria ?? {}) as Record<string, unknown>;
        return {
          ...e,
          modelo: { ...modelo, politicas: { ...politicas, lavadoDespuesDeHora: false } },
          configuracionAleatoria: { ...aleatoria, replicasAutomaticas: false },
        };
      }),
    };
  },
  2: (doc) => {
    const espacios = Array.isArray(doc.espacios) ? (doc.espacios as Record<string, unknown>[]) : [];
    return {
      ...doc,
      espacios: espacios.map((e) => {
        const modelo = (e.modelo ?? {}) as Record<string, unknown>;
        const proceso = (modelo.proceso ?? {}) as Record<string, unknown>;
        const tipos = Array.isArray(proceso.tiposDeUnidad)
          ? (proceso.tiposDeUnidad as Record<string, unknown>[])
          : [];
        return {
          ...e,
          modelo: {
            ...modelo,
            proceso: {
              ...proceso,
              tiposDeUnidad: tipos.map((t) => ({
                ...t,
                bahias: [
                  'mixto',
                  ...(t.id === 'mantenimiento' ? ['fast-lane'] : []),
                  ...(t.id === 'otros' ? ['mayor'] : []),
                ],
              })),
            },
          },
        };
      }),
    };
  },
  3: (doc) => {
    const espacios = Array.isArray(doc.espacios) ? (doc.espacios as Record<string, unknown>[]) : [];
    return {
      ...doc,
      espacios: espacios.map((e) => {
        const layout = (e.layout ?? {}) as Record<string, unknown>;
        return { ...e, layout: { ...layout, pisos: [], muros: [] } };
      }),
    };
  },
};

export class ErrorDeMigracion extends Error {}

export interface ResultadoMigracion {
  documento: Record<string, unknown>;
  desde: number;
  hasta: number;
}

export function migrarDocumento(
  documento: Record<string, unknown>,
  versionActual = VERSION_ESQUEMA,
  migraciones: Readonly<Record<number, Migracion>> = MIGRACIONES,
): ResultadoMigracion {
  const desde = documento.versionEsquema;
  if (typeof desde !== 'number' || !Number.isInteger(desde) || desde < 1) {
    throw new ErrorDeMigracion('El archivo no indica una versión de esquema válida.');
  }
  if (desde > versionActual) {
    throw new ErrorDeMigracion(
      `El archivo es de una versión más nueva de Gemba Studio (esquema ${desde}; esta aplicación usa el ${versionActual}). Actualiza la aplicación para abrirlo.`,
    );
  }
  let doc = structuredClone(documento);
  for (let v = desde; v < versionActual; v++) {
    const migrar = migraciones[v];
    if (!migrar) throw new ErrorDeMigracion(`Falta la migración del esquema ${v} al ${v + 1}.`);
    try {
      doc = { ...migrar(doc), versionEsquema: v + 1 };
    } catch (e) {
      throw new ErrorDeMigracion(
        `No se pudo convertir el proyecto del esquema ${v} al ${v + 1}: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }
  return { documento: doc, desde, hasta: versionActual };
}
